import React, { useMemo, useState } from 'react';
import { message, Upload } from 'antd';
import FetchUtils, { parseJSON } from '../../thunks/fetch_utils';
import { getBackendUri } from '../../utils/EnvConfigUtils';
import { connect } from 'react-redux';
import posthog from 'posthog-js';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';

const fetchUtils = new FetchUtils();

const OWUpload = (props) => {
	const [loading, setLoading] = useState<boolean>(false);
	const { uploaderType, userType } = props;
	const UploadComponent = useMemo(() => {
		if (uploaderType === 'dragger') {
			return Upload.Dragger;
		}
		return Upload;
	}, [uploaderType]);
	const userURL = useMemo(() => (userType ? `v1/${userType}` : `portal`), [userType]);
	return (
		<div>
			<UploadComponent
				{...props}
				disabled={loading}
				customRequest={({ file, onError, onSuccess, onProgress }) => {
					const fileType = file.type;
					const fileName = file.name;

					setLoading(true);
					const preSignedURLEndpoint = `${getBackendUri()}/api/${userURL}/file/upload/get_presigned_url`;
					fetchUtils
						.post(`${preSignedURLEndpoint}`, {
							data: {
								fileType,
								fileName,
							},
						})
						.then(parseJSON)
						.then(({ json }) => {
							const returnData = json.data;
							const signedRequest = returnData.presignedUrl;

							const uploadStartedAt = Date.now();

							// Use XMLHttpRequest for progress tracking
							const xhr = new XMLHttpRequest();

							// Track upload progress
							xhr.upload.onprogress = (event) => {
								if (event.lengthComputable) {
									const percent = Math.round((event.loaded / event.total) * 100);
									onProgress({ percent });
								}
							};

							xhr.onload = () => {
								const durationMs = Date.now() - uploadStartedAt;
								setLoading(false);
								if (xhr.status >= 200 && xhr.status < 300) {
									posthog.capture('file_upload_completed', {
										source: 'OWUpload',
										file_name: fileName,
										file_type: fileType,
										file_size: nullSafeGet('size', file),
										duration_ms: durationMs,
									});
									const onSuccessPayload = {
										data: returnData,
									};
									onSuccess(onSuccessPayload);
								} else {
									const error = new Error(`Upload failed with status ${xhr.status}`);
									posthog.capture('file_upload_failed', {
										source: 'OWUpload',
										file_name: fileName,
										file_type: fileType,
										file_size: nullSafeGet('size', file),
										duration_ms: durationMs,
										error: error.message,
									});
									onError && onError(error);
									message.error(`${fileName} file upload failed : ${error.message})`);
								}
							};

							xhr.onerror = () => {
								const durationMs = Date.now() - uploadStartedAt;
								setLoading(false);
								const error = new Error('Upload failed');
								posthog.capture('file_upload_failed', {
									source: 'OWUpload',
									file_name: fileName,
									file_type: fileType,
									file_size: nullSafeGet('size', file),
									duration_ms: durationMs,
									error: error.message,
								});
								onError && onError(error);
								message.error(`${fileName} file upload failed : ${error.message})`);
							};

							xhr.onabort = () => {
								const durationMs = Date.now() - uploadStartedAt;
								setLoading(false);
								const error = new Error('Upload cancelled');
								posthog.capture('file_upload_cancelled', {
									source: 'OWUpload',
									file_name: fileName,
									file_type: fileType,
									file_size: nullSafeGet('size', file),
									duration_ms: durationMs,
								});
								onError && onError(error);
							};

							xhr.open('PUT', signedRequest);
							xhr.setRequestHeader('Content-Type', fileType);
							xhr.send(file);
						})
						.catch((error) => {
							setLoading(false);
							onError && onError(error);
							message.error(`File Upload Failed : ${error && JSON.stringify(error)}`);
						})
						.finally(() => {
							setLoading(false);
						});
				}}
			/>
		</div>
	);
};
const mapStateToProps = (state) => {
	return {
		userType: state.session.userType,
	};
};

export default connect(mapStateToProps)(OWUpload);
